import React, { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { Printer, FileText, RefreshCw, ChevronDown, ChevronRight, Play, CheckCircle, XCircle, MinusCircle, X } from 'lucide-react';
import { dynamicApi } from '../../services/api';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import ReportPaginator from './components/ReportPaginator';
import wmLogo from '../../assets/WM-Logo-CB-Transparent.png';

// ─── Constants ────────────────────────────────────────────────────────────────
// 11×8.5 landscape at 96dpi
const PREVIEW_W = 1056;
const PREVIEW_H = 816;
const ROWS_PER_PAGE_1 = 10;   // first page has static sections, fewer rows
const ROWS_PER_PAGE   = 16;   // subsequent pages

// ─── Static document text ─────────────────────────────────────────────────────
const PREREQUISITES = [
  'Confirm equipment is in a safe state prior to performing any operation acceptance testing.',
  'Confirm QA/QC has been completed by electrical and/or mechanical subcontractor.'
];
const GENERAL_REQUIREMENTS = [
  'Confirm device installed in a reasonably serviceable fashion.',
  'Confirm device installed in accordance with manufacturers installation instructions and/or installation details.',
  'Confirm all cabling is labeled in accordance with project specification at each termination location.',
  'For all input devices confirm scaling and/or state change from the device to the HMI for all testing activities. If in the event HMI development is not complete testing may be performed and confirmed at the PLC. If testing is confirmed to the PLC tagging and scaling must be confirmed within the controller tags. Preferred method is to perform testing to the HMI.',
  'Confirm devices are not being used for control currently (typically only for early installations). If device is being used for control take remediation steps to eliminate risk of exposure to hazardous energies or undesirable process affects. Place control loops into manual or isolate hazardous energies and proceed with testing.',
  'Confirm there is no risk of exposure to hazardous energies or undesirable process affects by manipulating a device. Ensure hazardous energies are either isolated or understood to pose no risk.',
  'Confirm HMI tag name and description matches field device and IO database.',
  'Confirm engineering units and limits within the HMI match the device scaling where applicable.'
];
const CLOSE_OUT_INDIVIDUAL = [
  'Fix stainless steel tag to device (where applicable).',
  'Record device serial number (where applicable).',
  'Enter testing key used in OAT checklist in provided field for device tested.',
  'Enter initials in OAT checklist in provided field for device tested.'
];
const CLOSE_OUT_SYSTEM = [
  'Insert/append copies of the electrical contractor QA/QC checklist for the associated devices into the OAT binder.',
  'Insert/append copies of the calibration certificates for the associated devices into the OAT binder.',
  'File OAT package (OAT checklist, QA/QC documentation, calibration certificates) into the OAT file cabinet.',
  'Tune PID loops to respond to a 10% change in setpoint within a quarter wave decay response cycle.',
  'Modify Cimplicity resources from UNCOMM to the appropriate system level resources in the production HMI.',
  'Notify CxA of completion of OAT and coordinate equipment state (running or off).'
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseNumberedList(text) {
  if (!text) return [];
  const parts = text.split(/\s*\d+\.\s{1,3}/).filter(Boolean);
  return parts.length > 1 ? parts : [text];
}
function todayFormatted() {
  return new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

// Column widths from Word doc (twips → %) — 8 columns, total ~10.06"
const COL_WIDTHS = ['8.65%', '14.01%', '5.47%', '9.64%', '38.47%', '1.49%', '11.73%', '10.54%'];

// ─── DocHeader — exact Word template layout ───────────────────────────────────
const DocHeader = ({ owner, projectTitle, wmProject, clientLogoUrl }) => {
  const border = '1px solid #1A5568';
  const label = { fontSize: '8pt', fontFamily: 'Arial,sans-serif', color: '#111', padding: '3px 5px', whiteSpace: 'nowrap' };
  const val   = { fontSize: '8pt', fontFamily: 'Arial,sans-serif', padding: '3px 5px', minWidth: 0 };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', border }}>
      <colgroup>{COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
      <tbody>
        <tr>
          <td colSpan={2} style={{ border, padding: '4px 6px', verticalAlign: 'middle', textAlign: 'center' }}>
            <img src={wmLogo} alt="WM" style={{ height: '34px', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          </td>
          <td colSpan={3} style={{ border, padding: '3px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: '#111', lineHeight: 1.15, fontFamily: 'Arial,sans-serif' }}>Facilities Management &amp; Control System</div>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: '#111', lineHeight: 1.15, fontFamily: 'Arial,sans-serif' }}>Operational Acceptance Testing Procedures</div>
          </td>
          <td colSpan={3} style={{ border, padding: '4px 6px', verticalAlign: 'middle', textAlign: 'center' }}>
            {clientLogoUrl && (
              <img src={clientLogoUrl} alt="Client" style={{ maxHeight: '34px', maxWidth: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            )}
          </td>
        </tr>
        <tr>
          <td style={{ ...label, border }}>Owner:</td>
          <td colSpan={2} style={{ ...val, border }}>{owner}</td>
          <td style={{ ...label, border }}>Project Title:</td>
          <td colSpan={2} style={{ ...val, border }}>{projectTitle}</td>
          <td style={{ ...label, border }}>WM Project #:</td>
          <td style={{ ...val, border }}>{wmProject}</td>
        </tr>
      </tbody>
    </table>
  );
};

// ─── DocFooter — matches Word template footer ─────────────────────────────────
const DocFooter = ({ date, pageNum, totalPages }) => {
  const border = '1px solid #1A5568';
  const cell = { fontSize: '6.5pt', fontFamily: 'Arial,sans-serif', padding: '3px 6px', border };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '4px' }}>
      <colgroup>
        <col style={{ width: '7%' }} /><col style={{ width: '13%' }} />
        <col style={{ width: '60%' }} />
        <col style={{ width: '7%' }} /><col style={{ width: '13%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td style={cell}>Date:</td>
          <td style={cell}>{date}</td>
          <td style={{ ...cell, textAlign: 'center', fontStyle: 'italic', color: '#444' }}>Wunderlich-Malec Confidential</td>
          <td style={cell}>Page:</td>
          <td style={{ ...cell, textAlign: 'center' }}>{pageNum} of {totalPages}</td>
        </tr>
      </tbody>
    </table>
  );
};

// ─── Procedure Table ──────────────────────────────────────────────────────────
const ProcedureTable = ({ rows, showHeader }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '6.5pt', fontFamily: 'Arial,sans-serif', marginTop: showHeader ? '4px' : 0 }}>
    {showHeader && (
      <thead>
        <tr style={{ backgroundColor: '#1A5568', color: 'white' }}>
          <th style={{ padding: '4px 6px', textAlign: 'left', width: '13%', fontWeight: 700, borderRight: '1px solid #245e73' }}>Test Key</th>
          <th style={{ padding: '4px 6px', textAlign: 'left', width: '43.5%', fontWeight: 700, borderRight: '1px solid #245e73' }}>Testing Steps</th>
          <th style={{ padding: '4px 6px', textAlign: 'left', width: '43.5%', fontWeight: 700 }}>Required Results</th>
        </tr>
      </thead>
    )}
    <tbody>
      {rows.map((ck, i) => {
        const steps   = parseNumberedList(ck.testing_Steps);
        const results = parseNumberedList(ck.required_Results);
        const rowBg   = i % 2 === 0 ? '#fff' : '#f7fafc';
        return (
          <tr key={ck.id} style={{ backgroundColor: rowBg, verticalAlign: 'top', borderBottom: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 6px', fontWeight: 700, color: '#1A5568', whiteSpace: 'nowrap', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
              {ck.test_Key || '—'}
            </td>
            <td style={{ padding: '4px 6px', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
              {steps.length > 1
                ? <ol style={{ margin: 0, paddingLeft: '13px' }}>{steps.map((s, si) => <li key={si} style={{ marginBottom: '2px', lineHeight: 1.4 }}>{s.trim()}</li>)}</ol>
                : <span style={{ lineHeight: 1.4 }}>{steps[0]}</span>}
            </td>
            <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>
              {results.length > 1
                ? <ol style={{ margin: 0, paddingLeft: '13px' }}>{results.map((r, ri) => <li key={ri} style={{ marginBottom: '2px', lineHeight: 1.4 }}>{r.trim()}</li>)}</ol>
                : <span style={{ lineHeight: 1.4 }}>{results[0]}</span>}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

// ─── Section block (matches stui-step-panel) ────────────────────────────────
const StepPanel = ({ number, title, collapsed, onToggle, children, desc }) => (
  <div className="stui-step-panel" style={{ marginTop: '16px' }}>
    <div className="stui-step-header" onClick={onToggle} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span className="stui-step-number">{number}</span>
      <h3>{title}</h3>
      <span style={{ marginLeft: 'auto', color: 'var(--color-gray-400)' }}>
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
      </span>
    </div>
    {!collapsed && desc && <p className="stui-step-desc">{desc}</p>}
    {!collapsed && children}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const OATCommissioningKeysPage = () => {
  const { currentDb } = useDatabaseSafe() || {};

  // Data
  const [keys,    setKeys]    = useState([]);
  const [tags,    setTags]    = useState([]);
  const [sources, setSources] = useState([]);  // [{id, label, project_Desc, project_Number}]
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Parameters
  const [selectedSources, setSelectedSources] = useState([]);
  const [includeKeys,     setIncludeKeys]      = useState(true);

  // Run-time header values (set when report is run)
  const [runDate, setRunDate] = useState('');

  // UI state
  const [sec1Collapsed, setSec1Collapsed] = useState(false);
  const [sec2Collapsed, setSec2Collapsed] = useState(false);
  const [sec3Collapsed, setSec3Collapsed] = useState(false);
  const [hasRun,        setHasRun]        = useState(false);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [previewScale,  setPreviewScale]  = useState(1);

  const previewContainerRef = useRef(null);
  const previewContentRef   = useRef(null);

  // ── Derived header values ─────────────────────────────────────────────────
  const owner          = currentDb?.description || currentDb?.displayName || '';
  const clientLogoUrl  = currentDb?.iconUrl ? currentDb.iconUrl.replace('/icon.png', '/logo.png') : null;
  const activeSource   = selectedSources.length > 0 ? selectedSources[0] : sources[0];
  const projectTitle   = activeSource?.project_Desc || '';
  const wmProject      = activeSource?.project_Number || '';

  // ── Derived: source ID set for tag filtering ──────────────────────────────
  const sourceIds = useMemo(() => new Set(selectedSources.map(s => s.id)), [selectedSources]);

  // ── Derived: group tags by OAT_Key (for source filtering) ────────────────
  const tagsByKey = useMemo(() => {
    const map = {};
    tags.forEach(tag => {
      const k = tag.oAT_Key;
      if (k != null) { if (!map[k]) map[k] = []; map[k].push(tag); }
    });
    return map;
  }, [tags]);

  // ── Derived: filtered commissioning keys ──────────────────────────────────
  const filteredKeys = useMemo(() => {
    if (sourceIds.size === 0) return keys;
    return keys.filter(ck => (tagsByKey[ck.id] || []).some(t => sourceIds.has(t.source)));
  }, [keys, tagsByKey, sourceIds]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const pages = useMemo(() => {
    if (!includeKeys || filteredKeys.length === 0) return [[]];
    const result = [];
    result.push(filteredKeys.slice(0, ROWS_PER_PAGE_1));
    let i = ROWS_PER_PAGE_1;
    while (i < filteredKeys.length) {
      result.push(filteredKeys.slice(i, i + ROWS_PER_PAGE));
      i += ROWS_PER_PAGE;
    }
    return result;
  }, [filteredKeys, includeKeys]);

  const totalPages = pages.length;

  // ── Load sources on mount ─────────────────────────────────────────────────
  useEffect(() => {
    dynamicApi.getAll('Source').then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setSources(data.map(s => ({
        id: s.id,
        label: `${s.project_Number || ''} — ${s.project_Desc || ''}`.trim().replace(/^—\s*/, ''),
        project_Desc:   s.project_Desc   || '',
        project_Number: s.project_Number || '',
      })));
    }).catch(() => {});
  }, []);

  // ── Load commissioning keys ───────────────────────────────────────────────
  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dynamicApi.getAll('Commissioning_Key');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setKeys(data.sort((a, b) => (a.test_Key || '').localeCompare(b.test_Key || '')));
    } catch {
      setError('Failed to load commissioning keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load tags (lazy: only when source filter is active) ───────────────────
  const loadTags = useCallback(async () => {
    if (tags.length > 0) return;
    try {
      const res = await dynamicApi.getAll('Tags');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setTags(data);
    } catch { }
  }, [tags.length]);

  useEffect(() => {
    if (hasRun && sourceIds.size > 0) loadTags();
  }, [hasRun, sourceIds.size, loadTags]);

  // ── Run handler ───────────────────────────────────────────────────────────
  const handleRun = async () => {
    setRunDate(todayFormatted());
    await loadKeys();
    setCurrentPage(1);
    setHasRun(true);
    setSec2Collapsed(true);
    setSec3Collapsed(false);
  };

  // ── Scale preview to container ────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!hasRun) return;
    const update = () => {
      if (!previewContainerRef.current) return;
      setPreviewScale(previewContainerRef.current.clientWidth / PREVIEW_W);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [hasRun, currentPage]);

  // ── Source chip helpers ───────────────────────────────────────────────────
  const toggleSource = (src) => {
    setSelectedSources(prev =>
      prev.some(s => s.id === src.id) ? prev.filter(s => s.id !== src.id) : [...prev, src]
    );
    setCurrentPage(1);
  };
  const removeSource = (id) => {
    setSelectedSources(prev => prev.filter(s => s.id !== id));
    setCurrentPage(1);
  };

  // ── Current page data ─────────────────────────────────────────────────────
  const pageRows    = pages[currentPage - 1] || [];
  const isFirstPage = currentPage === 1;
  const isLastPage  = currentPage === totalPages;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @media print {
          .oat-toolbar, .oat-sections,
          .app-sidebar, .app-header, nav, header,
          [class*="sidebar"], [class*="Sidebar"],
          [class*="footer"]:not(.oat-doc-footer-tbl),
          [class*="Footer"]:not(.oat-doc-footer-tbl) { display: none !important; }
          body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
          @page { size: letter landscape; margin: 0.5in 0.6in 0.5in 0.6in; }
          .oat-all-pages { display: block !important; }
          .oat-print-page {
            page-break-after: always;
            break-after: page;
            padding: 0;
          }
          .oat-print-page:last-child { page-break-after: auto; break-after: auto; }
          .oat-field { border-bottom: none !important; }
          .oat-field::placeholder { color: transparent !important; }
        }
        @media screen {
          .oat-all-pages { display: none; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* ── Page title bar ──────────────────────────────────────────────── */}
      <div className="oat-toolbar" style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)'
      }}>
        <FileText size={20} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
          OAT Commissioning Keys
        </h1>
        {hasRun && !loading && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
            {filteredKeys.length} procedure{filteredKeys.length !== 1 ? 's' : ''}
            {selectedSources.length > 0 ? ` (filtered)` : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => window.print()}
          disabled={!hasRun || loading}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 600, opacity: (!hasRun || loading) ? 0.5 : 1 }}>
          <Printer size={14} /> Print / Export PDF
        </button>
      </div>

      {/* ── Sections ────────────────────────────────────────────────────── */}
      <div className="oat-sections" style={{ padding: '0 20px 20px' }}>

        {/* Section 1: Parameters */}
        <StepPanel number="1" title="Input Parameters" collapsed={sec1Collapsed} onToggle={() => setSec1Collapsed(c => !c)} desc="Filter procedures by Source. Enable 'Include Tags' to show associated devices under each procedure.">

          {/* Source parameter */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ paddingTop: '22px', flexShrink: 0 }}>
              {selectedSources.length === 0
                ? <MinusCircle size={16} style={{ color: 'var(--color-gray-400)' }} />
                : selectedSources.length <= 4
                  ? <CheckCircle size={16} style={{ color: '#22a355' }} />
                  : <XCircle size={16} style={{ color: '#dc3540' }} />}
            </div>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Source
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  style={{ fontSize: 'var(--text-sm)', border: '1px solid var(--color-gray-300)', borderRadius: '4px', padding: '6px 8px', minWidth: '280px', maxWidth: '420px', background: '#fff', color: 'var(--color-gray-700)', cursor: 'pointer' }}
                  onChange={e => {
                    const id = parseInt(e.target.value, 10);
                    const src = sources.find(s => s.id === id);
                    if (src && !selectedSources.some(s => s.id === id)) toggleSource(src);
                    e.target.value = '';
                  }}
                  value=""
                >
                  <option value="">— Select Source —</option>
                  {sources
                    .filter(s => !selectedSources.some(sel => sel.id === s.id))
                    .map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
            {selectedSources.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', paddingTop: '22px' }}>
                {selectedSources.map(s => (
                  <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', background: '#e3edf7', color: '#1f497d', border: '1px solid #b8d4f0' }}>
                    {s.label}
                    <X size={11} style={{ cursor: 'pointer', marginLeft: '2px', flexShrink: 0 }} onClick={() => removeSource(s.id)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Include OAT Commissioning Keys toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)', userSelect: 'none' }}>
              <input type="checkbox" checked={includeKeys} onChange={e => { setIncludeKeys(e.target.checked); setCurrentPage(1); }} style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
              Include OAT Commissioning Keys
            </label>
          </div>
        </StepPanel>

        {/* Section 2: Generate */}
        <StepPanel number="2" title="Generate" collapsed={sec2Collapsed} onToggle={() => setSec2Collapsed(c => !c)} desc="Load commissioning procedures and apply filters.">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleRun}
              disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 20px', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 600, opacity: loading ? 0.6 : 1 }}>
              {loading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
              {loading ? 'Loading…' : 'Run Report'}
            </button>
            {error && <span style={{ fontSize: 'var(--text-sm)', color: '#c53030' }}>{error}</span>}
          </div>
        </StepPanel>

        {/* Section 3: Preview */}
        <StepPanel number="3" title="Preview" collapsed={sec3Collapsed} onToggle={() => setSec3Collapsed(c => !c)} desc="Preview matches the print output. Use Print / Export PDF to generate the document.">

          {!hasRun && !loading && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#999' }}>
              <p style={{ fontSize: '15px' }}>Click <strong>Run Report</strong> to generate the preview.</p>
            </div>
          )}

          {hasRun && (
            <>
              {/* Gray background — paper-on-desk appearance */}
              <div style={{ background: '#c8cdd4', padding: '20px', borderRadius: '4px', overflow: 'hidden' }}>
                {/* Scale measured from this div — no padding so clientWidth = paper area */}
                <div ref={previewContainerRef} style={{ overflow: 'hidden' }}>
                  {/* Paper sheet: exact 8.5×11 at 96dpi, clipped so content never bleeds */}
                  <div
                    ref={previewContentRef}
                    style={{
                      transformOrigin: 'top left',
                      transform: `scale(${previewScale})`,
                      width: `${PREVIEW_W}px`,
                      height: `${PREVIEW_H}px`,
                      overflow: 'hidden',
                      marginBottom: `${(previewScale - 1) * PREVIEW_H}px`,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
                      /* match print margins: 0.5in top/bottom, 0.6in sides */
                      padding: '48px 58px 48px 58px',
                      boxSizing: 'border-box',
                      fontFamily: 'Arial, sans-serif',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Document header */}
                    <DocHeader owner={owner} projectTitle={projectTitle} wmProject={wmProject} clientLogoUrl={clientLogoUrl} />

                    {/* Page body — grows to fill available space, overflow clipped to paper */}
                    <div style={{ flex: 1, overflow: 'hidden', marginTop: '10px' }}>
                      {isFirstPage && (
                        <>
                          <div style={{ marginTop: '10px', marginBottom: '4px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                            <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Prerequisites</span>
                          </div>
                          <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                            {PREREQUISITES.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                          </ul>
                          <div style={{ marginTop: '10px', marginBottom: '4px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                            <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>General Requirements</span>
                          </div>
                          <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                            {GENERAL_REQUIREMENTS.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                          </ul>
                          {includeKeys && (
                            <div style={{ marginTop: '10px', marginBottom: '4px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                              <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Testing Procedures By Device Type</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Procedures table — only when includeKeys is checked */}
                      {includeKeys && (
                        filteredKeys.length === 0
                          ? <div style={{ padding: '12px 0', fontSize: '8pt', color: '#718096' }}>
                              {sourceIds.size > 0 ? 'No procedures found for the selected source(s).' : 'No commissioning keys found.'}
                            </div>
                          : <ProcedureTable rows={pageRows} showHeader={isFirstPage} />
                      )}

                      {isLastPage && (
                        <>
                          <div style={{ marginTop: '12px', marginBottom: '4px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                            <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Close Out Requirements</span>
                          </div>
                          <div style={{ fontSize: '6.5pt', fontWeight: 700, color: '#2d3748', marginTop: '4px', marginBottom: '2px' }}>After successful testing of an individual device:</div>
                          <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                            {CLOSE_OUT_INDIVIDUAL.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                          </ul>
                          <div style={{ fontSize: '6.5pt', fontWeight: 700, color: '#2d3748', marginTop: '8px', marginBottom: '2px' }}>After completion of all devices for a given piece of equipment or system:</div>
                          <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                            {CLOSE_OUT_SYSTEM.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                          </ul>
                        </>
                      )}
                    </div>

                    {/* Document footer — pinned to bottom of sheet */}
                    <div style={{ flexShrink: 0, paddingTop: '6px' }}>
                      <DocFooter date={runDate} pageNum={currentPage} totalPages={totalPages} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Paginator (outside scaled area, full size) */}
              <ReportPaginator
                currentPage={currentPage}
                totalPages={totalPages}
                totalRows={filteredKeys.length}
                pageSize={currentPage === 1 ? ROWS_PER_PAGE_1 : ROWS_PER_PAGE}
                onPageChange={p => { setCurrentPage(p); }}
              />
            </>
          )}
        </StepPanel>
      </div>

      {/* ── Print-only: all pages rendered ──────────────────────────────── */}
      <div className="oat-all-pages" style={{ fontFamily: 'Arial,sans-serif' }}>
        {pages.map((pageRowSet, pi) => {
          const pgNum   = pi + 1;
          const isFirst = pi === 0;
          const isLast  = pi === pages.length - 1;
          return (
            <div key={pi} className="oat-print-page" style={{ padding: '0', backgroundColor: 'white' }}>
              <DocHeader owner={owner} projectTitle={projectTitle} wmProject={wmProject} clientLogoUrl={clientLogoUrl} />
              <div style={{ marginTop: '8px' }}>
                {isFirst && (
                  <>
                    <div style={{ marginTop: '12px', marginBottom: '3px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                      <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Prerequisites</span>
                    </div>
                    <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                      {PREREQUISITES.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                    </ul>
                    <div style={{ marginTop: '10px', marginBottom: '3px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                      <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>General Requirements</span>
                    </div>
                    <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                      {GENERAL_REQUIREMENTS.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                    </ul>
                    {includeKeys && (
                      <div style={{ marginTop: '10px', marginBottom: '3px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                        <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Testing Procedures By Device Type</span>
                      </div>
                    )}
                  </>
                )}
                {includeKeys && <ProcedureTable rows={pageRowSet} showHeader={isFirst} />}
                {isLast && (
                  <>
                    <div style={{ marginTop: '12px', marginBottom: '3px', borderBottom: '1.5px solid #1A5568', paddingBottom: '2px' }}>
                      <span style={{ fontSize: '8pt', fontWeight: 700, color: '#1A5568', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Close Out Requirements</span>
                    </div>
                    <div style={{ fontSize: '6.5pt', fontWeight: 700, color: '#2d3748', marginTop: '4px', marginBottom: '2px' }}>After successful testing of an individual device:</div>
                    <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                      {CLOSE_OUT_INDIVIDUAL.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                    </ul>
                    <div style={{ fontSize: '6.5pt', fontWeight: 700, color: '#2d3748', marginTop: '8px', marginBottom: '2px' }}>After completion of all devices for a given piece of equipment or system:</div>
                    <ul style={{ margin: '3px 0', paddingLeft: '17px' }}>
                      {CLOSE_OUT_SYSTEM.map((t, i) => <li key={i} style={{ fontSize: '6.5pt', lineHeight: 1.45, marginBottom: '2px' }}>{t}</li>)}
                    </ul>
                  </>
                )}
              </div>
              <div style={{ marginTop: '8px' }}>
                <DocFooter date={runDate} pageNum={pgNum} totalPages={pages.length} />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default OATCommissioningKeysPage;
